import { AppError } from "../../../shared/utils/error-handler";
import type { OrderMailboxRepository } from "../../repositories/company/order-mailbox-repository";
import type {
  OrderMailboxListResponse,
  CollectedEmailDetail,
  CollectedEmail,
  CreateOrderFromEmailRequest,
  EstimateItemInput,
} from "../../types/order-mailbox";

export class OrderMailboxUsecase {
  constructor(private orderMailboxRepository: OrderMailboxRepository) {}

  async list(
    companyId: number,
    status?: string,
    keyword?: string,
    page?: number,
    size?: number,
  ): Promise<OrderMailboxListResponse> {
    try {
      return await this.orderMailboxRepository.list(
        companyId,
        status,
        keyword,
        page,
        size,
      );
    } catch (error) {
      throw new AppError(
        String(error) || "주문 메일 목록을 불러오지 못했습니다.",
        "ORDER_MAILBOX_LIST_FAILED",
        error,
      );
    }
  }

  async detail(companyId: number, id: number): Promise<CollectedEmailDetail> {
    try {
      return await this.orderMailboxRepository.detail(companyId, id);
    } catch (error) {
      throw new AppError(
        String(error) || "주문 메일 상세를 불러오지 못했습니다.",
        "ORDER_MAILBOX_DETAIL_FAILED",
        error,
      );
    }
  }

  async refresh(companyId: number): Promise<number> {
    try {
      return await this.orderMailboxRepository.refresh(companyId);
    } catch (error) {
      throw new AppError(
        String(error) || "메일 수집에 실패했습니다.",
        "ORDER_MAILBOX_REFRESH_FAILED",
        error,
      );
    }
  }

  async createOrder(
    companyId: number,
    id: number,
    request: CreateOrderFromEmailRequest,
  ): Promise<CollectedEmail> {
    try {
      return await this.orderMailboxRepository.createOrder(
        companyId,
        id,
        request,
      );
    } catch (error) {
      throw new AppError(
        String(error) || "주문 생성에 실패했습니다.",
        "ORDER_MAILBOX_CREATE_ORDER_FAILED",
        error,
      );
    }
  }

  async matchCustomer(
    companyId: number,
    id: number,
    userId: number,
  ): Promise<CollectedEmail> {
    try {
      return await this.orderMailboxRepository.matchCustomer(
        companyId,
        id,
        userId,
      );
    } catch (error) {
      throw new AppError(
        String(error) || "고객 연결에 실패했습니다.",
        "ORDER_MAILBOX_MATCH_CUSTOMER_FAILED",
        error,
      );
    }
  }

  async downloadEstimate(
    companyId: number,
    id: number,
    items: EstimateItemInput[],
  ): Promise<{ blob: Blob; fileName: string | null }> {
    try {
      return await this.orderMailboxRepository.downloadEstimate(
        companyId,
        id,
        items,
      );
    } catch (error) {
      throw new AppError(
        String(error) || "견적서 다운로드에 실패했습니다.",
        "ORDER_MAILBOX_DOWNLOAD_ESTIMATE_FAILED",
        error,
      );
    }
  }

  async sendEstimate(
    companyId: number,
    id: number,
    items: EstimateItemInput[],
    toEmail?: string | null,
  ): Promise<string> {
    try {
      return await this.orderMailboxRepository.sendEstimate(
        companyId,
        id,
        items,
        toEmail,
      );
    } catch (error) {
      throw new AppError(
        String(error) || "견적서 메일 전송에 실패했습니다.",
        "ORDER_MAILBOX_SEND_ESTIMATE_FAILED",
        error,
      );
    }
  }
}
